import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mountain,
  Link2,
  Unlink,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { basecampApi, organizationsApi, ImportMode } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { BasecampProjectBrowser } from './BasecampProjectBrowser';
import { BasecampMemberPreview } from './BasecampMemberPreview';
import { BasecampImportProgress, ImportSummary } from './BasecampImportProgress';

type Step = 'connect' | 'browse' | 'preview' | 'importing' | 'done';

export function BasecampIntegration() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [step, setStep] = useState<Step>('connect');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importOrgId, setImportOrgId] = useState<string | null>(null);
  const [pendingSelections, setPendingSelections] = useState<Map<number, ImportMode>>(new Map());
  const [importOrgName, setImportOrgName] = useState<string>('');
  const [projectOrgMap, setProjectOrgMap] = useState<Map<number, string>>(new Map());

  // Query connected accounts
  const { data: accountsData, isLoading: accountsLoading } = useQuery({
    queryKey: ['basecamp-accounts'],
    queryFn: () => basecampApi.getAccounts(),
  });

  const accounts = accountsData?.accounts || [];

  // When accounts load, determine initial step
  useEffect(() => {
    if (accounts.length > 0 && step === 'connect') {
      setSelectedAccountId(accounts[0].id);
      setStep('browse');
    }
  }, [accounts, step]);

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: (accountId: string) => basecampApi.disconnectAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['basecamp-accounts'] });
      setStep('connect');
      setSelectedAccountId(null);
    },
  });

  // OAuth flow
  const handleConnect = useCallback(async () => {
    setOauthError(null);
    try {
      const { url } = await basecampApi.getAuthUrl();
      // Open OAuth popup
      const popup = window.open(url, 'basecamp-oauth', 'width=600,height=700');
      if (!popup) {
        setOauthError('Please allow popups for this site to connect Basecamp.');
        return;
      }

      // Poll for popup closure and check for auth code
      const pollInterval = setInterval(async () => {
        try {
          if (popup.closed) {
            clearInterval(pollInterval);
            // Refresh accounts to see if connection was successful
            queryClient.invalidateQueries({ queryKey: ['basecamp-accounts'] });
          }
          // Try to read the popup URL for the callback code
          const popupUrl = popup.location?.href;
          if (popupUrl && popupUrl.includes('code=')) {
            clearInterval(pollInterval);
            const urlParams = new URL(popupUrl).searchParams;
            const code = urlParams.get('code');
            popup.close();
            if (code) {
              const result = await basecampApi.exchangeCode(code);
              if (result.success && result.accounts.length > 0) {
                queryClient.invalidateQueries({ queryKey: ['basecamp-accounts'] });
                setSelectedAccountId(result.accounts[0].id);
                setStep('browse');
              }
            }
          }
        } catch {
          // Cross-origin errors expected until redirect happens
        }
      }, 500);
    } catch (error) {
      setOauthError(error instanceof Error ? error.message : 'Failed to start OAuth flow');
    }
  }, [queryClient]);

  // Go to member preview step before importing
  const handleStartImport = useCallback(
    (selections: Map<number, ImportMode>, projOrgMap: Map<number, string>) => {
      if (!selectedAccountId) return;

      const userOrg = (user as any)?.organizations?.find(
        (org: any) => org.type === 'AGENCY',
      );
      const organizationId = userOrg?.id || userOrg?.organization?.id || '';

      if (!organizationId) {
        setOauthError('Could not determine organization ID');
        return;
      }

      setPendingSelections(selections);
      setProjectOrgMap(projOrgMap);
      setImportOrgId(organizationId);
      // Use the first project's org name for display purposes
      const orgNames = [...new Set(projOrgMap.values())];
      setImportOrgName(orgNames[0] || userOrg?.name || userOrg?.organization?.name || 'Default');
      setStep('preview');
    },
    [selectedAccountId, user],
  );

  // Actually start the import after member assignment is confirmed
  const handleConfirmAssignments = useCallback(
    async (assignments: Map<string, string>) => {
      if (!selectedAccountId || !importOrgId) return;

      const projects = Array.from(pendingSelections.entries()).map(([basecampId, importMode]) => ({
        basecampId,
        importMode,
      }));

      const orgAssignments = Array.from(assignments.entries()).map(([email, orgId]) => ({
        email,
        orgId,
      }));

      // Build project org overrides for orphaned projects that were assigned an org
      // Only include overrides for projects that don't have a parsedOrgName
      const projectOrgOverrides: Array<{ basecampId: number; orgName: string }> = [];
      for (const [basecampId, orgName] of projectOrgMap) {
        // This map includes both parsed orgs and user overrides;
        // the backend only needs overrides for projects without a parsed org name
        projectOrgOverrides.push({ basecampId, orgName });
      }

      try {
        const { jobId } = await basecampApi.startSelectiveImport({
          accountId: selectedAccountId,
          organizationId: importOrgId,
          projects,
          orgAssignments: orgAssignments.length > 0 ? orgAssignments : undefined,
          projectOrgOverrides: projectOrgOverrides.length > 0 ? projectOrgOverrides : undefined,
        });
        setActiveJobId(jobId);
        setStep('importing');
      } catch (error) {
        setOauthError(error instanceof Error ? error.message : 'Failed to start import');
      }
    },
    [selectedAccountId, importOrgId, pendingSelections, projectOrgMap],
  );

  const handleImportComplete = useCallback((summary: ImportSummary) => {
    setImportSummary(summary);
    setStep('done');
    // Refresh the org list in sidebar since import may have created new organizations
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    // Sync members to Accounts API so they appear in the Manage Members modal
    if (importOrgId) {
      organizationsApi.syncMembers(importOrgId).catch(() => {});
    }
  }, [queryClient, importOrgId]);

  const handleImportMore = useCallback(() => {
    setActiveJobId(null);
    queryClient.invalidateQueries({ queryKey: ['basecamp-projects'] });
    setStep('browse');
  }, [queryClient]);

  return (
    <Card className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
          <Mountain className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-heading-3 text-secondary">Basecamp</h2>
          <p className="text-sm text-grey-500">
            Import projects, tasks, and messages from Basecamp
          </p>
        </div>
        {accounts.length > 0 && step !== 'connect' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-grey-500">{accounts[0].name}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-primary hover:bg-primary/5"
              onClick={() => disconnectMutation.mutate(accounts[0].id)}
              disabled={disconnectMutation.isPending || step === 'importing'}
            >
              <Unlink className="w-3.5 h-3.5" />
              Disconnect
            </Button>
          </div>
        )}
      </div>

      {/* Error display */}
      {oauthError && (
        <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-grey-600">{oauthError}</p>
        </div>
      )}

      {/* Content based on step */}
      {accountsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      ) : step === 'connect' ? (
        <div className="space-y-4">
          <div className="p-4 bg-grey-50 rounded-lg">
            <p className="text-sm text-grey-600 mb-3">
              Connect your Basecamp account to import projects, tasks, messages, and files into Solid Hub.
            </p>
            <p className="text-sm text-grey-500">
              You'll be able to select which projects to import and choose how to handle previously imported data.
            </p>
          </div>
          <Button onClick={handleConnect}>
            <Link2 className="w-4 h-4" />
            Connect Basecamp
          </Button>
        </div>
      ) : step === 'browse' && selectedAccountId ? (
        <BasecampProjectBrowser
          accountId={selectedAccountId}
          onStartImport={handleStartImport}
        />
      ) : step === 'preview' && selectedAccountId && importOrgId ? (
        <BasecampMemberPreview
          accountId={selectedAccountId}
          selectedProjectIds={Array.from(pendingSelections.keys())}
          defaultOrgId={importOrgId}
          defaultOrgName={importOrgName}
          projectOrgMap={projectOrgMap}
          onConfirm={handleConfirmAssignments}
          onBack={() => setStep('browse')}
        />
      ) : step === 'importing' && activeJobId ? (
        <BasecampImportProgress
          jobId={activeJobId}
          onComplete={handleImportComplete}
          onCancel={() => setStep('browse')}
        />
      ) : step === 'done' ? (
        <div className="space-y-4">
          {importSummary && importSummary.totalErrors > 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-secondary mb-1">Import Completed with Errors</h3>
                <p className="text-sm text-grey-600">
                  {importSummary.totalImported} imported, {importSummary.totalSkipped} skipped, {importSummary.totalErrors} errors.
                  Some items could not be imported.
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-secondary mb-1">Import Complete</h3>
                <p className="text-sm text-grey-600">
                  Your Basecamp data has been imported successfully.
                  {importSummary && ` ${importSummary.totalImported} items imported.`}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Button onClick={handleImportMore}>
              Import More Projects
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/projects'}
            >
              View Projects
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
